import { Seo } from '@frontend/shared/web';
import { zodResolver } from '@hookform/resolvers/zod';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

import { FormField } from '../../components/common/FormField';
import { PublicShell } from '../../components/layout/PublicShell';
import { toUserErrorMessage } from '../../lib/apiError';
import { useExtranetLogin } from '../../services/api/hooks/useExtranetLogin';

const schema = z.object({
  email: z.string().trim().email({ message: 'Email invalide' }),
  password: z.string().trim().min(1, { message: 'Mot de passe requis' }),
});

type LoginFormValues = z.infer<typeof schema>;

export const LoginRoute = () => {
  const form = useForm<LoginFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = form;

  const { mutate: login, isPending, error } = useExtranetLogin();

  const onSubmit: SubmitHandler<LoginFormValues> = ({ email, password }) => {
    login({ email, password });
  };

  return (
    <>
      <Seo title="Connexion — Portail extranet" />

      <PublicShell
        title="Portail extranet"
        subtitle="Connectez-vous pour piloter vos prestations de transport."
        footer="Besoin d'aide ? Contactez votre administrateur Allocab."
      >
        <form
          className="flex flex-col gap-3 bg-white border border-zinc-200 rounded-lg p-6 shadow-sm"
          onSubmit={handleSubmit(onSubmit)}
        >
          <FormField
            id="email"
            type="email"
            label="Email"
            placeholder="jean.dupont@allocab.com"
            autoComplete="email"
            {...register('email')}
            error={errors.email?.message}
          />
          <FormField
            id="password"
            type="password"
            label="Mot de passe"
            placeholder="••••••••"
            autoComplete="current-password"
            {...register('password')}
            error={errors.password?.message}
          />

          {error ? (
            <p className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2 mt-1" role="alert">
              {toUserErrorMessage(error, 'Connexion impossible').description}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={isPending}
            className="h-10 w-full rounded-md bg-zinc-900 px-4 text-sm font-medium text-white transition-colors duration-100 hover:bg-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 mt-2"
          >
            {isPending ? 'Connexion…' : 'Se connecter'}
          </button>
        </form>
      </PublicShell>
    </>
  );
};
